import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton, Text } from '@repletoryreach/sdk-ui';
import { Alert, Timeline } from '@repletoryreach/sdk-ui/data-display';
import { inbox, channelWa } from '@repletoryreach/sdk-api';
import type { ConversationMessage, MessageDetail, TemplateParam } from '@repletoryreach/sdk-api/inbox';
import { formatInTz } from '@repletoryreach/sdk-utils/date';
import { copyText } from '@repletoryreach/sdk-utils/clipboard';
import { formatBytes } from '@repletoryreach/sdk-media';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useCredits } from '../../../lib/credits';
import { useT, useI18n, type Translate } from '../../../i18n/I18nProvider';
import { useAgentOptions } from './TransferModal';

export interface MessageDetailsProps {
  conversationId: string;
  /** The row the thread already has, so the panel draws immediately and fills in. */
  message: ConversationMessage;
  timezone: string;
  onClose: () => void;
  /**
   * Drop the panel's own title bar. Set when a drawer is already providing
   * one, so a narrow window does not get two titles and two close buttons.
   */
  hideHeader?: boolean;
  /** Who Meta bills for the conversation's number: on the business's own account a billing refusal is theirs to fix. */
  metaPayer?: channelWa.MetaBillingPayer;
}

/**
 * Everything the platform knows about one message.
 *
 * The receipts have been on `messages` since the inbox shipped and were shown
 * nowhere: a message delivered at 12:24:00 and read at 12:24:14 looked exactly
 * like one that had gone nowhere, and so did a failure, and so did what it
 * cost. This is where an agent finds out.
 *
 * It is a column beside the thread rather than a sheet over it, because the
 * question it answers is always "what happened to THAT message?" and the
 * message has to stay on screen while it is answered. On a narrow window there
 * is no room for a third column and the inbox page puts this in a drawer
 * instead.
 */
export function MessageDetails({ conversationId, message, timezone, onClose, hideHeader, metaPayer }: MessageDetailsProps) {
  const t = useT();
  const { locale } = useI18n();
  const credits = useCredits();
  const { user } = useAuth();
  const { options: agents } = useAgentOptions();
  const closeRef = useRef<HTMLButtonElement>(null);

  const q = useQuery({
    queryKey: ['conversation', conversationId, 'message', message.id],
    queryFn: () => inbox.getMessage(conversationId, message.id),
    // A receipt or a refund can land after the panel is open, and both are the
    // reason somebody opened it.
    refetchInterval: 20_000
  });

  // Opening the panel moves focus into it, so the keyboard reaches the close
  // control and everything after it without tabbing back through the thread.
  useEffect(() => { closeRef.current?.focus(); }, [message.id]);

  const d: MessageDetail | null = q.data?.data ?? null;
  const outbound = message.direction === 'outbound';

  return (
    <section
      className="rr-inbox2__details"
      aria-label={t('inbox.details.title')}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
    >
      {!hideHeader && (
        <header className="rr-inbox2__details-head">
          <Text role="bodySmall" fw={600}>{t('inbox.details.title')}</Text>
          <button
            type="button"
            ref={closeRef}
            className="rr-inbox2__details-close"
            onClick={onClose}
            aria-label={t('inbox.details.close')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
      )}

      <div className="rr-inbox2__details-body">
        {q.isError && <Alert color="error" role="alert">{t('inbox.details.error')}</Alert>}
        {q.isPending && !q.isError && (
          <div aria-busy="true" aria-label={t('common.loading')}>
            <Skeleton height={12} width="60%" />
            <Skeleton height={12} width="85%" />
            <Skeleton height={12} width="45%" />
          </div>
        )}

        {d && (
          <>
            <DeliveryTimeline detail={d} outbound={outbound} timezone={timezone} locale={locale} t={t} />
            <Failure detail={d} t={t} metaPayer={metaPayer} />
            {outbound && <Cost detail={d} timezone={timezone} credits={credits} t={t} />}
            <WhatWasSent detail={d} t={t} />
            <WhereItWent
              detail={d}
              outbound={outbound}
              t={t}
              senderName={senderNameOf(d, user?.id ?? null, agents, t)}
            />
            <ForSupport detail={d} t={t} />
          </>
        )}
      </div>
    </section>
  );
}

// ── Timeline ───────────────────────────────────────────────────────────────

interface Stage { key: string; label: string; at: string }

/**
 * The stages that actually happened, each with the time it happened and how
 * long after the previous one.
 *
 * Only real stages are drawn. A stage that has not happened is not a greyed
 * step waiting its turn, because two of them may never happen at all and
 * drawing them as pending says the opposite: a message can be delivered and
 * never read, and a read receipt exists only where the recipient has read
 * receipts switched on. That last point is said out loud under the list rather
 * than left to be inferred, because the inference people make is "they ignored
 * me".
 */
function DeliveryTimeline({
  detail, outbound, timezone, locale, t
}: { detail: MessageDetail; outbound: boolean; timezone: string; locale: string; t: Translate }) {
  const stages: Stage[] = [];
  stages.push({
    key: 'sent',
    label: outbound ? t('inbox.details.timeline.sent') : t('inbox.details.timeline.received'),
    at: detail.created_at
  });
  if (detail.delivered_at) stages.push({ key: 'delivered', label: t('inbox.details.timeline.delivered'), at: detail.delivered_at });
  if (detail.read_at) stages.push({ key: 'read', label: t('inbox.details.timeline.read'), at: detail.read_at });
  if (detail.failed_at) stages.push({ key: 'failed', label: t('inbox.details.timeline.failed'), at: detail.failed_at });

  const start = new Date(detail.created_at).getTime();

  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">{t('inbox.details.timeline')}</h3>
      <Timeline active={stages.length - 1} bulletSize={14} lineWidth={2}>
        {stages.map((s, i) => (
          <Timeline.Item key={s.key}>
            <div className="rr-inbox2__details-stage">
              <span className="rr-inbox2__details-stage-label">{s.label}</span>
              {/* No explicit locale: the app store already holds the reader's
                  own (`en-ZA`, `af-ZA`, …) and passing the bare language here
                  turned every time in the panel American while the tooltip on
                  the bubble beside it stayed South African. */}
              <span className="rr-inbox2__details-stage-time">{formatInTz(s.at, { timeZone: timezone })}</span>
              {i > 0 && (
                <span className="rr-inbox2__details-stage-gap">
                  {t('inbox.details.timeline.later', { duration: gap(new Date(s.at).getTime() - start, locale) })}
                </span>
              )}
            </div>
          </Timeline.Item>
        ))}
      </Timeline>

      {outbound && !detail.failed_at && !detail.read_at && (
        <p className="rr-inbox2__details-note">
          {detail.delivered_at ? t('inbox.details.read.absent') : t('inbox.details.delivered.pending')}
        </p>
      )}
    </section>
  );
}

/**
 * "14 seconds", in the reader's language.
 *
 * `Intl.NumberFormat` with a unit style rather than `RelativeTimeFormat`: the
 * relative formatter would produce "in 14 seconds", and this is a gap between
 * two past events, not a distance from now. The wrapping "… later" is the
 * catalogue's, because word order is not ours to assume.
 */
function gap(ms: number, locale: string): string {
  const abs = Math.max(0, ms);
  const [unit, size] =
    abs < 60_000 ? (['second', 1000] as const)
    : abs < 3_600_000 ? (['minute', 60_000] as const)
    : abs < 86_400_000 ? (['hour', 3_600_000] as const)
    : (['day', 86_400_000] as const);
  const value = Math.max(1, Math.round(abs / size));
  try {
    return new Intl.NumberFormat(`${locale}-ZA`, { style: 'unit', unit, unitDisplay: 'long' }).format(value);
  } catch {
    return `${value} ${unit}`;
  }
}

// ── Why it failed ──────────────────────────────────────────────────────────

/**
 * The reason, in the reader's language, and what to do about it.
 *
 * The refusal code is channel-wa's own (`MetaRefusals.ForDelivery`), which
 * matches on Meta's wording as well as its number, so it is preferred over
 * re-deriving one from the numeric code here. Meta's own sentence is shown
 * last and marked as Meta's: it is in English, in Meta's register, and it is
 * what support will quote, but it is not the sentence the reader needs first.
 */
function Failure({ detail, t, metaPayer }: { detail: MessageDetail; t: Translate; metaPayer?: channelWa.MetaBillingPayer }) {
  if (!detail.failed_at) return null;
  const refusal = detail.error_refusal_code ?? channelWa.deliveryFailure(detail.error_code).code;
  // On the business's own WhatsApp account a billing refusal is theirs to fix in Meta, and says so.
  const code = refusal === 'ACCOUNT_NOT_PAYABLE' && metaPayer === 'customer' ? 'ACCOUNT_NOT_PAYABLE.customer' : refusal;
  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">{t('inbox.details.why')}</h3>
      <Alert color="error" role="status">
        <span className="rr-inbox2__details-why">
          {t(`inbox.message.failed.${code}`, detail.error_title ?? t('inbox.message.failed'))}
        </span>
        <span className="rr-inbox2__details-next">{t(`inbox.message.failed.${code}.next`, '')}</span>
      </Alert>
      <dl className="rr-inbox2__details-list">
        {detail.error_code && <Row label={t('inbox.details.why.metaCode')} value={detail.error_code} mono />}
        {detail.error_title && <Row label={t('inbox.details.why.metaWords')} value={detail.error_title} />}
      </dl>
    </section>
  );
}

// ── What it cost ───────────────────────────────────────────────────────────

function Cost({
  detail, timezone, credits, t
}: { detail: MessageDetail; timezone: string; credits: (minor: number) => string; t: Translate }) {
  const cost = detail.cost ?? null;
  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">{t('inbox.details.cost')}</h3>
      {!cost ? (
        // Never "free". The ledger may simply not have been readable, and a
        // message that cost something reported as costing nothing is the one
        // mistake a billing figure must not make.
        <p className="rr-inbox2__details-note">{t('inbox.details.cost.unknown')}</p>
      ) : (
        <>
          <p className="rr-inbox2__details-amount">{credits(cost.amount_minor)}</p>
          <dl className="rr-inbox2__details-list">
            {cost.sku_code && <Row label={t('inbox.details.cost.rate')} value={cost.sku_code} mono />}
            {cost.rate_minor != null && cost.units != null && (
              <Row
                label={t('inbox.details.cost.worked')}
                value={t('inbox.details.cost.workedValue', { units: String(cost.units), rate: credits(cost.rate_minor) })}
              />
            )}
            {cost.charged_at && (
              <Row label={t('inbox.details.cost.chargedAt')} value={formatInTz(cost.charged_at, { timeZone: timezone })} />
            )}
          </dl>
          {cost.refunded && (
            <Alert color="success" role="status">
              {t('inbox.details.cost.refunded', { amount: credits(cost.refunded_minor ?? cost.amount_minor) })}
            </Alert>
          )}
        </>
      )}
    </section>
  );
}

// ── What was sent ──────────────────────────────────────────────────────────

function WhatWasSent({ detail, t }: { detail: MessageDetail; t: Translate }) {
  const facts = detail.template_facts ?? null;
  const params = facts?.params ?? [];
  const hasTemplate = !!detail.template_name;
  const hasFile = !!detail.media_id || !!detail.media_url || !!detail.media_name;
  if (!hasTemplate && !hasFile) return null;

  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">{t('inbox.details.sent')}</h3>
      <dl className="rr-inbox2__details-list">
        {detail.template_name && <Row label={t('inbox.details.template')} value={detail.template_name} mono />}
        {detail.template_language && <Row label={t('inbox.details.language')} value={detail.template_language} />}
        {detail.template_category && (
          <Row
            label={t('inbox.details.category')}
            value={t(`templates.category.${detail.template_category}`, detail.template_category)}
          />
        )}
        {detail.body_source === 'recovered' && <Row label={t('inbox.details.wording')} value={t('inbox.details.wording.recovered')} />}
        {detail.body_source === 'unrecoverable' && <Row label={t('inbox.details.wording')} value={t('inbox.details.wording.lost')} />}
        {detail.media_name && <Row label={t('inbox.details.file.name')} value={detail.media_name} />}
        {detail.media_mime && <Row label={t('inbox.details.file.type')} value={detail.media_mime} mono />}
        {detail.media_size != null && <Row label={t('inbox.details.file.size')} value={formatBytes(detail.media_size)} />}
      </dl>

      {params.length > 0 && (
        <>
          <h4 className="rr-inbox2__details-subheading">{t('inbox.details.params')}</h4>
          <ul className="rr-inbox2__details-params">
            {params.map((p: TemplateParam, i: number) => (
              <li key={`${p.component}-${p.index}-${i}`}>
                <span className="rr-inbox2__details-param-slot">
                  {t(`inbox.details.param.${p.component}`, p.component)} {p.index}
                </span>
                <span className="rr-inbox2__details-param-value">{p.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ── Where it went ──────────────────────────────────────────────────────────

function WhereItWent({
  detail, outbound, senderName, t
}: { detail: MessageDetail; outbound: boolean; senderName: string | null; t: Translate }) {
  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">
        {outbound ? t('inbox.details.where') : t('inbox.details.whereFrom')}
      </h3>
      <dl className="rr-inbox2__details-list">
        {outbound ? (
          <>
            <Row
              label={t('inbox.details.to')}
              value={detail.contact_display_name
                ? `${detail.contact_display_name} · ${detail.to_msisdn ?? detail.contact_msisdn ?? ''}`
                : detail.to_msisdn ?? detail.contact_msisdn ?? ''}
            />
            {detail.from_msisdn && <Row label={t('inbox.details.from')} value={detail.from_msisdn} />}
            {senderName && <Row label={t('inbox.details.sentBy')} value={senderName} />}
          </>
        ) : (
          <>
            <Row
              label={t('inbox.details.from')}
              value={detail.contact_display_name
                ? `${detail.contact_display_name} · ${detail.from_msisdn ?? detail.contact_msisdn ?? ''}`
                : detail.from_msisdn ?? detail.contact_msisdn ?? ''}
            />
            {detail.to_msisdn && <Row label={t('inbox.details.to')} value={detail.to_msisdn} />}
          </>
        )}
      </dl>
    </section>
  );
}

/**
 * Who sent it, in words rather than in ids.
 *
 * The platform knows one of three things: this was you, this was a named
 * colleague, or this was something the platform did on the account's behalf.
 * A raw user id in place of a person's name is not an answer, so the fallback
 * is "a team member" rather than the guid.
 */
function senderNameOf(
  detail: MessageDetail,
  currentUserId: string | null,
  agents: { user_id: string; label: string }[],
  t: Translate
): string | null {
  if (detail.sent_by_user_id) {
    if (currentUserId && detail.sent_by_user_id === currentUserId) return t('inbox.details.sentBy.you');
    return agents.find((a) => a.user_id === detail.sent_by_user_id)?.label ?? t('inbox.details.sentBy.teammate');
  }
  const actor = detail.sent_by_actor ?? '';
  if (actor.startsWith('system:flows')) return t('inbox.details.sentBy.bot');
  if (actor.startsWith('system:broadcasts')) return t('inbox.details.sentBy.broadcast');
  if (actor.startsWith('system:')) return t('inbox.details.sentBy.platform');
  return null;
}

// ── For support ────────────────────────────────────────────────────────────

/**
 * Meta's own id, copyable. It is the one thing Meta's support asks for, and
 * before this it existed only in the database.
 */
function ForSupport({ detail, t }: { detail: MessageDetail; t: Translate }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!detail.meta_message_id) return null;
  return (
    <section className="rr-inbox2__details-section">
      <h3 className="rr-inbox2__details-heading">{t('inbox.details.support')}</h3>
      <div className="rr-inbox2__details-id">
        <code>{detail.meta_message_id}</code>
        <Button
          size="xs"
          variant="light"
          onClick={() => { void copyText(detail.meta_message_id!).then(() => setCopied(true)); }}
        >
          {copied ? t('inbox.details.copied') : t('inbox.details.copy')}
        </Button>
      </div>
      <p className="rr-inbox2__details-note">{t('inbox.details.metaId.hint')}</p>
    </section>
  );
}

// ── Shared ─────────────────────────────────────────────────────────────────

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rr-inbox2__details-row">
      <dt>{label}</dt>
      <dd className={mono ? 'is-mono' : undefined}>{value}</dd>
    </div>
  );
}
